import React from 'react';
import {
  Box, Chip, Paper, Stack, Typography,
} from '@mui/material';
import { useStargraveEquipment } from '../../stores/stargraveCustomStore';
import { formatStargraveSoldierCategory } from '../../game/stargraveSoldierRules';
import { useWarbandStore } from '../../stores/warbandStore';
import { miniFlexSx } from '../../App';
import { StatColumn } from '../shared/StatColumn';
import { IndexGroupHeader, IndexLayout } from '../shared/IndexLayout';
import { PowerCard } from '../shared/PowerCard';
import { MiniCardExpand } from '../shared/MiniCardExpand';
import {
  searchStargraveIndex, StargraveIndexFilter, StargraveIndexEntry,
} from '../../game/stargraveIndex';

const categories: Array<{ value: StargraveIndexFilter; label: string }> = [
  { value: 'all', label: 'All' },
  { value: 'powers', label: 'Powers' },
  { value: 'soldiers', label: 'Soldiers' },
  { value: 'equipment', label: 'Equipment' },
  { value: 'special', label: 'Special Equipment' },
  { value: 'ship', label: 'Ship Upgrades' },
];

const specialGroupLabels: Partial<Record<StargraveIndexEntry['category'], string>> = {
  advancedWeapons: 'Advanced Weapons',
  technology: 'Advanced Technology',
  artefacts: 'Alien Artefacts',
};

const StatLine: React.FC<{ item: Extract<StargraveIndexEntry, { category: 'soldiers' }>['item'] }> = ({ item }) => (
  <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(6, minmax(36px, 1fr))', gap: 1, my: 1.5 }}>
    {(['move', 'fight', 'shoot', 'armour', 'will', 'health'] as const).map((stat) => <StatColumn
      key={stat}
      label={stat.slice(0, 1).toUpperCase()}
      value={item[stat]}
      adjustedValue={item[stat]}
    />)}
  </Box>
);

const IndexCard: React.FC<{ entry: StargraveIndexEntry; minified?: boolean }> = ({ entry, minified = false }) => {
  const stargraveEquipment = useStargraveEquipment();
  if (entry.category === 'powers') {
    return <PowerCard
      name={entry.item.name}
      activation={entry.item.activation}
      strain={entry.item.strain}
      timing={entry.item.timing}
      summary={entry.item.summary}
      minified={minified}
    />;
  }
  if (minified) {
    const miniSx = { p: 1.5, width: 280, minHeight: 96, boxSizing: 'border-box' as const };
    const signed = (value: number) => `${value >= 0 ? '+' : ''}${value}`;
    let chip: string | null = null;
    let meta: string | null = null;
    let caption: string | null = null;
    switch (entry.category) {
      case 'soldiers': {
        const soldier = entry.item;
        chip = formatStargraveSoldierCategory(soldier.category);
        meta = `M ${soldier.move} · F ${signed(soldier.fight)} · S ${signed(soldier.shoot)} · A ${soldier.armour} · W ${signed(soldier.will)} · H ${soldier.health}`;
        caption = soldier.cost === 0 ? 'Free' : `${soldier.cost}cr`;
        break;
      }
      case 'equipment':
        chip = entry.item.category;
        meta = [
          `${entry.item.gearSlots} slot${entry.item.gearSlots === 1 ? '' : 's'}`,
          entry.item.damageModifier !== undefined ? `Damage ${signed(entry.item.damageModifier)}` : null,
          entry.item.maxRange !== undefined ? `Range ${entry.item.maxRange}"` : null,
          entry.item.armourModifier !== undefined ? `Armour ${signed(entry.item.armourModifier)}` : null,
          entry.item.moveModifier !== undefined ? `Move ${entry.item.moveModifier}` : null,
        ].filter(Boolean).join(' | ');
        break;
      case 'technology':
        caption = `Buy ${entry.item.cost}cr | Sell ${entry.item.salePrice}cr | ${entry.item.gearSlots} slot${entry.item.gearSlots === 1 ? '' : 's'}`;
        break;
      case 'advancedWeapons':
        caption = `Buy ${entry.item.cost}cr | Sell ${entry.item.salePrice}cr`;
        break;
      case 'artefacts':
        caption = `Buy ${entry.item.cost}cr | Sell ${entry.item.salePrice}cr | Leaders only`;
        break;
      default:
        caption = `${entry.item.cost}cr`;
    }
    return <Paper variant="outlined" sx={miniSx}>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" gap={1}>
        <Typography variant="subtitle2" noWrap sx={{ fontWeight: 700, minWidth: 0, flex: 1 }}>{entry.item.name}</Typography>
        {chip && <Chip size="small" label={chip} />}
        <MiniCardExpand label={entry.item.name} fullCard={<IndexCard entry={entry} />} />
      </Stack>
      {meta && <Typography variant="caption" component="div" sx={{ mt: 0.5 }}>{meta}</Typography>}
      {caption && <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.5 }}>{caption}</Typography>}
    </Paper>;
  }

  if (entry.category === 'soldiers') {
    return <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack direction="row" justifyContent="space-between" gap={1}>
        <Typography variant="h6">{entry.item.name}</Typography>
        <Chip size="small" label={formatStargraveSoldierCategory(entry.item.category)} />
      </Stack>
      <StatLine item={entry.item} />
      <Typography variant="body2" color="text.secondary">{entry.item.cost === 0 ? 'Free' : `${entry.item.cost}cr`}</Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
        {entry.item.equipmentIds.map((id) => <Chip key={id} size="small" label={stargraveEquipment.find((item) => item.id === id)?.name ?? id} />)}
      </Box>
      {entry.item.notes && <Typography variant="body2" sx={{ mt: 1 }}>{entry.item.notes}</Typography>}
    </Paper>;
  }

  if (entry.category === 'equipment') {
    const details = [
      `${entry.item.gearSlots} slot${entry.item.gearSlots === 1 ? '' : 's'}`,
      entry.item.damageModifier !== undefined ? `Damage ${entry.item.damageModifier >= 0 ? '+' : ''}${entry.item.damageModifier}` : null,
      entry.item.maxRange !== undefined ? `Range ${entry.item.maxRange}\"` : null,
      entry.item.armourModifier !== undefined ? `Armour ${entry.item.armourModifier >= 0 ? '+' : ''}${entry.item.armourModifier}` : null,
      entry.item.moveModifier !== undefined ? `Move ${entry.item.moveModifier}` : null,
    ].filter(Boolean);
    return <Paper variant="outlined" sx={{ p: 2 }}>
      <Typography variant="h6">{entry.item.name}</Typography>
      <Chip size="small" label={entry.item.category} sx={{ my: 1 }} />
      <Typography variant="body2" color="text.secondary">{details.join(' | ')}</Typography>
      {entry.item.notes && <Typography variant="body2" sx={{ mt: 1 }}>{entry.item.notes}</Typography>}
    </Paper>;
  }

  if (entry.category === 'technology') {
    return <Paper variant="outlined" sx={{ p: 2 }}>
      <Typography variant="h6">{entry.item.name}</Typography>
      <Typography variant="body2" sx={{ my: 1 }}>{entry.item.summary}</Typography>
      {entry.item.restriction && <Typography variant="body2" color="text.secondary">{entry.item.restriction}</Typography>}
      <Typography variant="caption" color="text.secondary">Buy {entry.item.cost}cr | Sell {entry.item.salePrice}cr | {entry.item.gearSlots} slot{entry.item.gearSlots === 1 ? '' : 's'}</Typography>
    </Paper>;
  }

  if (entry.category === 'advancedWeapons') {
    return <Paper variant="outlined" sx={{ p: 2 }}>
      <Typography variant="h6">{entry.item.name}</Typography>
      <Typography variant="body2" sx={{ my: 1 }}>{entry.item.summary}</Typography>
      <Typography variant="caption" color="text.secondary">Buy {entry.item.cost}cr | Sell {entry.item.salePrice}cr | Indestructible</Typography>
    </Paper>;
  }

  if (entry.category === 'artefacts') {
    return <Paper variant="outlined" sx={{ p: 2 }}>
      <Typography variant="h6">{entry.item.name}</Typography>
      <Typography variant="body2" sx={{ my: 1 }}>{entry.item.summary}</Typography>
      <Typography variant="caption" color="text.secondary">Buy {entry.item.cost}cr | Sell {entry.item.salePrice}cr | Leaders only</Typography>
    </Paper>;
  }

  return <Paper variant="outlined" sx={{ p: 2 }}>
    <Typography variant="h6">{entry.item.name}</Typography>
    <Typography variant="body2" sx={{ my: 1 }}>{entry.item.summary}</Typography>
    <Typography variant="caption" color="text.secondary">{entry.item.cost}cr</Typography>
  </Paper>;
};

export const StargraveIndexTab: React.FC = () => {
  const [category, setCategory] = React.useState<StargraveIndexFilter>('all');
  const [query, setQuery] = React.useState('');
  const isMinified = useWarbandStore((state) => state.isMinified);
  const deferredQuery = React.useDeferredValue(query);
  const entries = React.useMemo(() => searchStargraveIndex(deferredQuery, category), [deferredQuery, category]);

  const gridChildren = React.useMemo(() => {
    const children: React.ReactNode[] = [];
    let lastGroup = '';
    for (const entry of entries) {
      const group = specialGroupLabels[entry.category] ?? '';
      if (group && group !== lastGroup) {
        children.push(<IndexGroupHeader key={`group-${group}`} label={group} />);
      }
      lastGroup = group;
      children.push(<IndexCard key={`${entry.category}-${entry.id}`} entry={entry} minified={isMinified} />);
    }
    return children;
  }, [entries, isMinified]);

  return (
    <IndexLayout
      query={query}
      onQueryChange={setQuery}
      searchPlaceholder="Search Stargrave references"
      categories={categories}
      category={category}
      onCategoryChange={setCategory}
      isEmpty={entries.length === 0}
      emptyMessage="No references match your search."
      gridSx={isMinified ? miniFlexSx : undefined}
    >
      {gridChildren}
    </IndexLayout>
  );
};
